import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { styled } from "@mui/material";
import { ThreeDotsLoader } from "fe-web-components/loader/threeDotsLoader";
import { ExternalBackgroundOverlay } from "../uiControllers/extrenalBackgroundOverlay";
import Slide from "@mui/material/Slide";
import { commonBoxShadowStyle } from "fe-common/ui/boxShadow";
import { PatientColorMap } from "../../layout/patientColorMap";
import Button from "@mui/material/Button";
import CloseIconButton from "fe-common/ui/icons/closeIconButton";
import { useTranslate } from "../../hooks/useTranslate";
import { ReactComponent as CopyIcon } from "../../assets/copy.svg";
import { ReactComponent as EmailIcon } from "../../assets/email.svg";
import TextField from "@mui/material/TextField";
import { ActionButton } from "../uiControllers/CommonStyled";
import { safeCopyToClipboard } from "fe-common/utils/safeCopyToClipboard";
import { usePatient } from "../../state/patientContext";
import Fade from "@mui/material/Fade";
import { GENDER_FEMALE_FEATURE_ID } from "fe-common/graphql/dao";
import { usePatientFeatureFlags } from "../../hooks/usePatientFeatureFlags";
import { useNativePatientShare } from "./useNativePatientShare";
import { onRtl } from "fe-web-components/rtl/rtlStyles";

//region [[ Styles ]]

const ShareWindowView = styled(ExternalBackgroundOverlay)({
  justifyContent: "center",
  alignItems: "center",
});

const ShareWindowBox = styled("div")({
  display: "flex",
  flexFlow: "column",
  background: "white",
  position: "absolute",
  top: "51px",
  borderRadius: "10px",
  padding: "23px",
  ...commonBoxShadowStyle,
  width: "75%",
});

const CloseButton = styled(CloseIconButton)({
  "&.close-button": {
    right: "19px",
    left: "auto",
    top: "14px",
    color: PatientColorMap.Default,
    "& svg": {
      width: "21px",
      height: "21px",
    },
  },
});

const Title = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  color: PatientColorMap.Default,
  fontSize: "21px",
  lineHeight: "22px",
  fontWeight: 700,
  marginBottom: "40px",
  marginTop: "17px",
});

const ShareLinkButton = styled(Button)({
  background: "white",
  "& svg": {
    marginRight: "12px",
  },
  ...onRtl({
    "& svg": {
      marginRight: 0,
      marginLeft: "12px",
    },
  }),
  justifyContent: "flex-start",
  textTransform: "none",
});

const CopyLinkImage = styled(CopyIcon)({});

const CopyLinkTextField = styled(TextField)({
  background: PatientColorMap.Background,
  color: PatientColorMap.Primary,
  "& input": {
    padding: "10px 19px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: PatientColorMap.Primary,
    direction: "ltr",
  },
});

const EmailButton = styled(Button)({
  background: PatientColorMap.Background,
  color: PatientColorMap.Primary,
  padding: "10px 19px",
  textTransform: "none",
});

const GoToAgent = styled(ShareLinkButton)({
  marginTop: "24px",
  justifyContent: "center",
});

const Separator = styled("div")({
  margin: "21px",
  display: "flex",
  flexFlow: "fow",
  justifyContent: "center",
  alignItems: "center",
});

const CopiedNotification = styled("div")({
  position: "absolute",
  bottom: 0,
  left: "8%",
  width: "80%",
  paddingBottom: "6px",
  textAlign: "center",
});

const NotificationTextContainer = styled("div")({
  color: PatientColorMap.DefaultLight,
  background: PatientColorMap.PrimaryDark,
  borderRadius: "14px",
  padding: "12px",
  width: "calc(100% - 16px)",
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface ShareWindowProps {
  className?: string;
  chiefComplaint?: string;
  windowOnly?: boolean;
  onClose?: () => void;
  open?: boolean;
}

//endregion [[ Props ]]

//region [[ Texts ]]
const SHARE_CASE = "SHARE_CASE";
const COPY_LINK = "COPY_LINK";
const OR = "OR";
const SEND_TO_DOCTOR_TEXT = "SEND_TO_DOCTOR_TEXT";
const SEND_EMAIL = "SEND_EMAIL";
const EMAIL_BUTTON_TEXT = "EMAIL_BUTTON_TEXT";
const LINK_WAS_COPIED_TO_CLIPBOARD = "LINK_WAS_COPIED_TO_CLIPBOARD";
const GO_TO_AGENT = "GO_TO_AGENT";
//endregion [[ Texts ]]

const NOTIFICATION_TIME_MS = 3 * 1000; // Show it for 3 seconds

export const ShareWindow = ({
  chiefComplaint,
  windowOnly,
  onClose,
  open,
  ...props
}: ShareWindowProps) => {
  const shareBoxRef = useRef<HTMLDivElement>();

  const { translate } = useTranslate();

  const {
    externalShareIsSupported,
    openExternalShare,
    shareData,
    loading,
  } = useNativePatientShare();

  const [customWindowOpen, setCustomWindowOpen] = useState(false);

  const handleCloseClicked = useCallback(() => {
    setCustomWindowOpen(false);
    onClose?.();
  }, [setCustomWindowOpen, onClose]);

  const [showNotification, setShowNotification] = useState(false);

  const { age, gender: genderId } = usePatient();

  const gender = useMemo(
    () => (genderId === GENDER_FEMALE_FEATURE_ID ? "Female" : "Male"),
    [genderId],
  );

  useEffect(() => {
    if (!loading && open && !externalShareIsSupported) {
      setCustomWindowOpen(true);
    }
  }, [loading, open, externalShareIsSupported]);

  useEffect(() => {
    if (showNotification)
      setTimeout(() => setShowNotification(false), NOTIFICATION_TIME_MS);
  }, [showNotification, setShowNotification]);

  const navigateToAgent = useCallback(() => {
    if (!shareData) return;
    window.open(shareData?.url, "_blank");
  }, [shareData]);

  const handleCopyClicked = useCallback(() => {
    // TODO: copy the text + url to clipboard
    safeCopyToClipboard(shareData?.url).then(() => setShowNotification(true));
  }, [setShowNotification, shareData]);

  const handleEmailClicked = useCallback(() => {
    const emailTitle = `Patient1st assessment - ${age}YO ${gender} with ${chiefComplaint ||
      ""} - ${new Date().toLocaleString()}`;

    window.open(
      `mailto:your@doctor.com?subject=${encodeURIComponent(
        emailTitle,
      )}&body=${encodeURIComponent(shareData?.text)}`,
      "_blank",
    );
  }, [shareData, age, gender, chiefComplaint]);

  const handleLinkClicked = useCallback(
    (e) => {
      handleCopyClicked();
      e?.traget?.focus();
      e?.traget?.setSelectionRange(0, e?.traget?.value?.length);
    },
    [handleCopyClicked],
  );

  const isDebug = usePatientFeatureFlags("debugSheet");

  // Make sure to scroll into the popup once it's opened, give it some time to render
  useEffect(() => {
    if (!loading && customWindowOpen) {
      setTimeout(() => {
        if (shareBoxRef.current) {
          shareBoxRef.current.scrollIntoView({
            behavior: "smooth",
            inline: "center",
            block: "center",
          });
        }
      }, 500);
    }
  }, [loading, customWindowOpen]);

  const handleShareClick = useCallback(() => {
    // if we are still loading give 0.5s to finish
    if (loading) return setTimeout(() => handleShareClick, 500);
    if (externalShareIsSupported) {
      openExternalShare()
        // .then((result) => console.log(result))
        .catch((e) => (isDebug ? alert(e) : console.error(e)));
    } else {
      setCustomWindowOpen(true);
    }
  }, [loading, externalShareIsSupported, isDebug, openExternalShare]);

  if (windowOnly && !customWindowOpen) return null;

  if (!customWindowOpen) {
    return (
      <ActionButton
        onClick={handleShareClick}
        label={translate(SEND_TO_DOCTOR_TEXT)}
      />
    );
  }

  if (loading) return <ThreeDotsLoader color={PatientColorMap.Primary} />;

  return (
    <ShareWindowView className={props.className}>
      <Slide in direction={"up"} mountOnEnter unmountOnExit>
        <ShareWindowBox className={"share-window-box"} ref={shareBoxRef}>
          <CloseButton onClick={handleCloseClicked} />
          <Title>{translate(SHARE_CASE)}</Title>
          <ShareLinkButton onClick={handleCopyClicked}>
            <CopyLinkImage />
            {translate(COPY_LINK)}
          </ShareLinkButton>
          <CopyLinkTextField
            onClick={handleLinkClicked}
            value={shareData?.url}
            disabled
          />
          <Separator>{translate(OR)}</Separator>
          <ShareLinkButton onClick={handleEmailClicked}>
            <EmailIcon />
            {translate(SEND_EMAIL)}
          </ShareLinkButton>
          <EmailButton onClick={handleEmailClicked}>
            {translate(EMAIL_BUTTON_TEXT)}
          </EmailButton>
          {isDebug && (
            <GoToAgent onClick={navigateToAgent}>
              {translate(GO_TO_AGENT)}
            </GoToAgent>
          )}
          <CopiedNotification>
            <Fade in={showNotification} mountOnEnter unmountOnExit>
              <NotificationTextContainer>
                {translate(LINK_WAS_COPIED_TO_CLIPBOARD)}
              </NotificationTextContainer>
            </Fade>
          </CopiedNotification>
        </ShareWindowBox>
      </Slide>
    </ShareWindowView>
  );
};
